import { useState, useEffect, useMemo, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { hasCapability } from "@/lib/permissions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Mail, Search, Send, RotateCcw, Save, ChevronRight, Eye, Users as UsersIcon,
  AtSign, Zap, CheckCircle2, XCircle, Info, Bold, Italic, Link as LinkIcon,
  List, ListOrdered, Loader2, Sparkles, MessageCircle, Phone, Clock, FileJson,
  RefreshCw, PlugZap,
} from "lucide-react";

const ROLES = [
  { key: "admin", label: "Admin" },
  { key: "admin_addl", label: "Admin_addl" },
  { key: "manager", label: "Manager" },
  { key: "purchase_manager", label: "Purchase Manager" },
  { key: "admin_manager", label: "Admin Manager" },
  { key: "accounts_manager", label: "Accounts Manager" },
  { key: "accounts_assistant", label: "Accounts Assistant" },
  { key: "stores_manager", label: "Stores Manager" },
  { key: "stores_incharge", label: "Store Keeper" },
  { key: "maintenance_manager", label: "Maintenance Manager" },
  { key: "technician", label: "Technician" },
  { key: "line_supervisor", label: "Line Supervisor" },
  { key: "gate_keeper", label: "Gate Keeper" },
  { key: "logistics_supervisor", label: "Logistics Supervisor" },
  { key: "hr_manager", label: "HR Manager" },
];

const authHeaders = () => ({});

export default function EmailTemplates() {
  const { user } = useAuth();
  const canManage = hasCapability(user, "manage_email_templates");

  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedKey, setSelectedKey] = useState(null);
  const [search, setSearch] = useState("");
  const [waStatus, setWaStatus] = useState(null); // { configured, base_url, default_country_code, key_masked }

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/email-templates`, authHeaders());
      setTemplates(r.data.templates);
      setCategories(r.data.categories);
      if (!selectedKey && r.data.templates.length) setSelectedKey(r.data.templates[0].trigger_key);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load templates");
    } finally {
      setLoading(false);
    }
  };

  const loadWaStatus = async () => {
    try {
      const r = await api.get(`/admin/whatsapp/status`, authHeaders());
      setWaStatus(r.data);
    } catch {
      setWaStatus({ configured: false });
    }
  };

  useEffect(() => { load(); loadWaStatus(); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return templates;
    return templates.filter(
      (t) => t.label.toLowerCase().includes(s) || t.trigger_key.includes(s) || t.category.toLowerCase().includes(s)
    );
  }, [templates, search]);

  const grouped = useMemo(() => {
    const byCat = {};
    filtered.forEach((t) => { (byCat[t.category] = byCat[t.category] || []).push(t); });
    return byCat;
  }, [filtered]);

  if (!canManage) {
    return (
      <div className="p-8">
        <Alert className="border-amber-200 bg-amber-50">
          <Info className="w-4 h-4 !text-amber-600" />
          <AlertDescription className="text-amber-900">
            You don&apos;t have permission to manage email templates. Ask an administrator to grant the
            &ldquo;<code className="font-mono">manage_email_templates</code>&rdquo; capability to your role.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8" data-testid="email-templates-page">
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-heading font-semibold text-slate-900 flex items-center gap-2">
            <Mail className="w-6 h-6 text-[#7C1D3A]" />
            Communication Configuration
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Control the subject, body, recipients and WhatsApp fan-out for every automated notification Asset IQ sends.
            Use <code className="font-mono bg-slate-100 px-1 rounded text-xs">{"{{token}}"}</code> placeholders
            that get replaced with real record data at send time.
          </p>
        </div>
        <WhatsAppStatusBadge status={waStatus} onRefresh={loadWaStatus} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6">
        {/* Left: trigger list */}
        <aside className="space-y-3">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search triggers..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8"
              data-testid="tpl-search"
            />
          </div>
          {loading ? (
            <div className="text-sm text-slate-500 text-center py-6">Loading...</div>
          ) : (
            <div className="space-y-4 max-h-[75vh] overflow-y-auto pr-1">
              {categories.map((cat) => {
                const items = grouped[cat];
                if (!items || items.length === 0) return null;
                return (
                  <div key={cat}>
                    <div className="text-[10px] font-semibold tracking-widest uppercase text-slate-500 mb-1.5">
                      {cat}
                    </div>
                    <div className="space-y-1">
                      {items.map((t) => (
                        <button
                          key={t.trigger_key}
                          onClick={() => setSelectedKey(t.trigger_key)}
                          data-testid={`tpl-item-${t.trigger_key}`}
                          className={`w-full text-left rounded-md border px-3 py-2 transition ${
                            selectedKey === t.trigger_key
                              ? "border-[#7C1D3A] bg-[#7C1D3A]/5"
                              : "border-slate-200 hover:border-slate-300 bg-white"
                          }`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="text-sm font-medium text-slate-900 truncate">{t.label}</div>
                            <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />
                          </div>
                          <div className="flex items-center gap-1.5 mt-1">
                            {!t.wired && (
                              <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 border-amber-300 text-amber-700 bg-amber-50">
                                Not yet firing
                              </Badge>
                            )}
                            {!t.enabled && (
                              <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 border-slate-300 text-slate-600 bg-slate-50">
                                Disabled
                              </Badge>
                            )}
                            {t.wa_enabled && (
                              <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 border-emerald-300 text-emerald-700 bg-emerald-50 gap-0.5">
                                <MessageCircle className="w-2.5 h-2.5" /> WA
                              </Badge>
                            )}
                            <span className="text-[10px] text-slate-500 ml-auto">
                              {t.recipient_count} recipient{t.recipient_count !== 1 ? "s" : ""}
                            </span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </aside>

        {/* Right: editor */}
        <main>
          {selectedKey && (
            <TemplateEditor
              triggerKey={selectedKey}
              onSaved={load}
              adminEmail={user?.email}
              waConfigured={waStatus?.configured}
            />
          )}
        </main>
      </div>
    </div>
  );
}


function TemplateEditor({ triggerKey, onSaved, adminEmail, waConfigured }) {
  const [detail, setDetail] = useState(null);
  const [users, setUsers] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewSubject, setPreviewSubject] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testSending, setTestSending] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const bodyRef = useRef(null);

  useEffect(() => {
    setDirty(false); setTestResult(null); setPreviewHtml("");
    (async () => {
      const [r1, r2] = await Promise.all([
        api.get(`/email-templates/${triggerKey}`, authHeaders()),
        users.length ? Promise.resolve({ data: users }) : api.get(`/users`, authHeaders()),
      ]);
      setDetail(r1.data);
      setUsers(r2.data);
      // Start the email test-send field empty so admins can dry-run to any
      // address (Ops team, personal Gmail, a stakeholder) instead of always
      // firing to their own profile email.
      setTestTo("");
      refreshPreview(r1.data);
    })().catch((e) => toast.error(e.response?.data?.detail || "Failed to load template"));
    // eslint-disable-next-line
  }, [triggerKey]);

  const refreshPreview = async (data = detail) => {
    if (!data) return;
    setPreviewLoading(true);
    try {
      const r = await api.post(
        `/email-templates/${data.trigger_key}/preview`,
        { subject: data.subject, body_html: data.body_html },
        authHeaders()
      );
      setPreviewSubject(r.data.subject);
      setPreviewHtml(r.data.body_html);
    } catch (e) {
      setPreviewSubject("(preview failed)");
      setPreviewHtml(`<div style="color:#dc2626">Preview error: ${e.response?.data?.detail || e.message}</div>`);
    } finally {
      setPreviewLoading(false);
    }
  };

  const patch = (u) => { setDetail((d) => ({ ...d, ...u })); setDirty(true); };

  const setRcp = (bucket, k, v) => {
    // `bucket` is "to" or "cc"; `k` is one of roles/user_ids/fixed_emails/dynamic_tokens.
    setDetail((d) => {
      const rc = d.recipients || { to: {}, cc: {} };
      const cur = rc[bucket] || {};
      return {
        ...d,
        recipients: {
          ...rc,
          [bucket]: { ...cur, [k]: v },
        },
      };
    });
    setDirty(true);
  };

  const setWa = (k, v) => {
    setDetail((d) => ({ ...d, whatsapp: { ...(d.whatsapp || {}), [k]: v } }));
    setDirty(true);
  };

  const insertToken = (token) => {
    const el = bodyRef.current;
    if (!el) return;
    const start = el.selectionStart ?? detail.body_html.length;
    const end = el.selectionEnd ?? detail.body_html.length;
    const before = detail.body_html.slice(0, start);
    const after = detail.body_html.slice(end);
    const inserted = `{{${token}}}`;
    patch({ body_html: before + inserted + after });
    setTimeout(() => {
      el.focus();
      el.selectionStart = el.selectionEnd = start + inserted.length;
    }, 0);
  };

  const wrapSelection = (openTag, closeTag) => {
    const el = bodyRef.current;
    if (!el) return;
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;
    const selected = detail.body_html.slice(start, end);
    const before = detail.body_html.slice(0, start);
    const after = detail.body_html.slice(end);
    const replaced = `${openTag}${selected || "text"}${closeTag}`;
    patch({ body_html: before + replaced + after });
    setTimeout(() => {
      el.focus();
      el.selectionStart = start + openTag.length;
      el.selectionEnd = start + openTag.length + (selected || "text").length;
    }, 0);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(
        `/email-templates/${triggerKey}`,
        {
          subject: detail.subject,
          body_html: detail.body_html,
          recipients: detail.recipients,
          enabled: detail.enabled,
          whatsapp: detail.whatsapp ? {
            enabled: !!detail.whatsapp.enabled,
            template_id: (detail.whatsapp.template_id || "").trim(),
            variable_map: detail.whatsapp.variable_map || "",
          } : undefined,
          schedule_days_before: detail.supports_schedule_days_before
            ? (detail.schedule_days_before || [0])
            : undefined,
        },
        authHeaders()
      );
      toast.success("Template saved");
      setDirty(false);
      onSaved && onSaved();
      refreshPreview();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const resetToDefault = async () => {
    if (!confirm("Reset this template to the shipped default? Your subject, body, and recipient tweaks will be lost.")) return;
    try {
      await api.post(`/email-templates/${triggerKey}/reset`, {}, authHeaders());
      toast.success("Reset to default");
      // Reload
      const r = await api.get(`/email-templates/${triggerKey}`, authHeaders());
      setDetail(r.data);
      setDirty(false);
      onSaved && onSaved();
      refreshPreview(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset failed");
    }
  };

  const sendTest = async () => {
    if (dirty) {
      toast.info("Saving your changes before sending the test...");
      await save();
    }
    setTestSending(true); setTestResult(null);
    try {
      const r = await api.post(`/email-templates/${triggerKey}/test-send`, { to: testTo }, authHeaders());
      setTestResult(r.data);
      if (r.data.ok) toast.success("Test sent — check your inbox");
      else toast.error("Test send failed — see details below");
    } catch (e) {
      setTestResult({ ok: false, error: e.response?.data?.detail || e.message });
    } finally {
      setTestSending(false);
    }
  };

  if (!detail) return <Card><CardContent className="p-8 text-center text-slate-500">Loading template...</CardContent></Card>;

  return (
    <div className="space-y-4" data-testid="tpl-editor">
      {/* Header card */}
      <Card>
        <CardContent className="p-5 flex flex-wrap items-start gap-4">
          <div className="flex-1 min-w-[280px]">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-heading font-semibold text-slate-900" data-testid="tpl-label">{detail.label}</h2>
              {!detail.wired && (
                <Badge variant="outline" className="border-amber-300 text-amber-700 bg-amber-50 text-[10px]">
                  Not yet firing
                </Badge>
              )}
            </div>
            <p className="text-xs text-slate-500 mt-1">{detail.description}</p>
            <p className="text-[11px] text-slate-400 mt-1 font-mono">{detail.trigger_key}</p>
            {!detail.wired && (
              <p className="text-xs text-amber-700 mt-2">
                <Sparkles className="w-3.5 h-3.5 inline mr-1" />
                Template is configurable now — the actual event emit-point ships in Phase 2.
              </p>
            )}
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Switch
                checked={detail.enabled}
                onCheckedChange={(v) => patch({ enabled: v })}
                data-testid="tpl-enabled"
              />
              <span className="text-xs font-medium text-slate-600">{detail.enabled ? "Enabled" : "Disabled"}</span>
            </div>
            <Button variant="outline" size="sm" onClick={resetToDefault} data-testid="tpl-reset-btn">
              <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Reset
            </Button>
            <Button
              onClick={save}
              disabled={!dirty || saving}
              className="bg-[#7C1D3A] hover:bg-[#6c1c36]"
              data-testid="tpl-save-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Reminder schedule (days-before-due) — only for triggers that support it */}
      {detail.supports_schedule_days_before && (
        <ScheduleDaysBeforeCard
          value={detail.schedule_days_before || detail.default_schedule_days_before || [0]}
          defaults={detail.default_schedule_days_before || [0]}
          onChange={(next) => patch({ schedule_days_before: next })}
        />
      )}

      {/* Subject */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-heading flex items-center gap-2">
            <AtSign className="w-4 h-4 text-[#7C1D3A]" /> Subject line
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0 space-y-2">
          <Input
            value={detail.subject || ""}
            onChange={(e) => patch({ subject: e.target.value })}
            className="font-mono text-sm"
            data-testid="tpl-subject"
          />
          <TokenChips tokens={detail.tokens} onInsert={(t) => {
            const el = document.querySelector('[data-testid="tpl-subject"]');
            if (!el) return;
            const s = el.selectionStart ?? (detail.subject || "").length;
            const e = el.selectionEnd ?? (detail.subject || "").length;
            const v = detail.subject || "";
            patch({ subject: v.slice(0, s) + `{{${t}}}` + v.slice(e) });
          }} />
        </CardContent>
      </Card>

      {/* Body + preview */}
      <Card>
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-sm font-heading flex items-center gap-2">
            <Mail className="w-4 h-4 text-[#7C1D3A]" /> Body (HTML)
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={() => refreshPreview()} disabled={previewLoading} data-testid="tpl-refresh-preview">
            <Eye className="w-3.5 h-3.5 mr-1.5" /> {previewLoading ? "Rendering..." : "Refresh preview"}
          </Button>
        </CardHeader>
        <CardContent className="pt-0 space-y-3">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <div className="flex items-center gap-1 mb-2 border-b border-slate-100 pb-2">
                <ToolbarBtn onClick={() => wrapSelection("<b>", "</b>")} icon={Bold} label="Bold" />
                <ToolbarBtn onClick={() => wrapSelection("<i>", "</i>")} icon={Italic} label="Italic" />
                <ToolbarBtn onClick={() => wrapSelection('<a href="https://">', "</a>")} icon={LinkIcon} label="Link" />
                <ToolbarBtn onClick={() => wrapSelection("<ul>\n  <li>", "</li>\n</ul>")} icon={List} label="Bulleted list" />
                <ToolbarBtn onClick={() => wrapSelection("<ol>\n  <li>", "</li>\n</ol>")} icon={ListOrdered} label="Numbered list" />
              </div>
              <Textarea
                ref={bodyRef}
                value={detail.body_html || ""}
                onChange={(e) => patch({ body_html: e.target.value })}
                className="font-mono text-xs h-[380px] resize-none"
                spellCheck={false}
                data-testid="tpl-body"
              />
              <TokenChips tokens={detail.tokens} onInsert={insertToken} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-2 flex items-center gap-1">
                <Eye className="w-3 h-3" /> Live preview (with sample data)
              </div>
              <div className="border border-slate-200 rounded-md overflow-hidden bg-white">
                <div className="bg-slate-50 border-b border-slate-200 px-3 py-2 text-xs">
                  <div className="text-slate-400 text-[10px]">Subject</div>
                  <div className="font-medium text-slate-900 truncate" data-testid="tpl-preview-subject">
                    {previewSubject || "(empty)"}
                  </div>
                </div>
                <iframe
                  title="preview"
                  srcDoc={`<html><head><style>body{font-family:Arial,Helvetica,sans-serif;font-size:14px;color:#0f172a;padding:16px;margin:0;}p{margin:0 0 12px}</style></head><body>${previewHtml}</body></html>`}
                  className="w-full h-[380px] border-0 bg-white"
                  data-testid="tpl-preview-iframe"
                  sandbox=""
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Recipients */}
      <RecipientsCard
        detail={detail}
        users={users}
        setRcp={setRcp}
      />

      {/* WhatsApp (Automate.chats) */}
      <WhatsAppPanel
        detail={detail}
        setWa={setWa}
        saveParent={save}
        dirty={dirty}
        waConfigured={waConfigured}
      />

      {/* Test send */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-heading flex items-center gap-2">
            <Send className="w-4 h-4 text-[#7C1D3A]" /> Send test email
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0 space-y-3">
          <p className="text-xs text-slate-500">
            Renders this template with the sample values shown above and delivers it via Google Workspace SMTP Relay.
            Enter <b>any address</b> below for a dry-run — your own inbox, a stakeholder, an ops shared mailbox. If you have unsaved changes, they will be saved first.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <Input
              type="email"
              value={testTo}
              onChange={(e) => setTestTo(e.target.value)}
              placeholder="Enter any email to test — e.g. you@example.com"
              className="max-w-md"
              data-testid="tpl-test-to"
            />
            <Button
              onClick={sendTest}
              disabled={testSending || !testTo}
              className="bg-[#7C1D3A] hover:bg-[#6c1c36]"
              data-testid="tpl-test-send-btn"
            >
              {testSending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Send test
            </Button>
          </div>
          {testResult && (testResult.ok ? (
            <Alert className="border-emerald-200 bg-emerald-50" data-testid="tpl-test-ok">
              <CheckCircle2 className="w-4 h-4 !text-emerald-600" />
              <AlertDescription className="text-emerald-900 text-sm">
                Delivered · Message-ID <code className="font-mono">{testResult.result}</code>
              </AlertDescription>
            </Alert>
          ) : (
            <Alert className="border-rose-200 bg-rose-50" data-testid="tpl-test-fail">
              <XCircle className="w-4 h-4 !text-rose-600" />
              <AlertDescription className="text-rose-900 text-sm">
                <div className="font-semibold mb-1">Delivery failed</div>
                <div className="font-mono text-xs bg-white/70 border border-rose-200 rounded p-2 whitespace-pre-wrap break-words">
                  {testResult.error}
                </div>
              </AlertDescription>
            </Alert>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}


function ToolbarBtn({ onClick, icon: Icon, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      className="p-1.5 rounded hover:bg-slate-100 text-slate-600 transition"
    >
      <Icon className="w-3.5 h-3.5" />
    </button>
  );
}


function TokenChips({ tokens, onInsert }) {
  if (!tokens?.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      <span className="text-[10px] text-slate-400 self-center mr-1">Insert token:</span>
      {tokens.map((t) => (
        <button
          key={t.key}
          type="button"
          onClick={() => onInsert(t.key)}
          title={t.description}
          className="text-[10px] font-mono px-1.5 py-0.5 rounded border border-slate-200 bg-slate-50 hover:bg-[#7C1D3A]/10 hover:border-[#7C1D3A]/40 hover:text-[#7C1D3A] transition"
          data-testid={`tpl-token-${t.key}`}
        >
          {"{{"}
          {t.key}
          {"}}"}
        </button>
      ))}
    </div>
  );
}


function RecipientsCard({ detail, users, setRcp }) {
  // Canonical shape: detail.recipients = { to: {...}, cc: {...} }.
  // Guard against legacy flat shape (fold into `to`) so the UI never crashes.
  const rc = detail.recipients || {};
  const toBucket = rc.to || {
    roles: rc.roles || [],
    user_ids: rc.user_ids || [],
    fixed_emails: rc.fixed_emails || [],
    dynamic_tokens: rc.dynamic_tokens || [],
  };
  const ccBucket = rc.cc || { roles: [], user_ids: [], fixed_emails: [], dynamic_tokens: [] };

  const toCount = (toBucket.roles?.length || 0) + (toBucket.user_ids?.length || 0)
                + (toBucket.fixed_emails?.length || 0) + (toBucket.dynamic_tokens?.length || 0);
  const ccCount = (ccBucket.roles?.length || 0) + (ccBucket.user_ids?.length || 0)
                + (ccBucket.fixed_emails?.length || 0) + (ccBucket.dynamic_tokens?.length || 0);

  return (
    <Card data-testid="recipients-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-heading flex items-center gap-2">
          <UsersIcon className="w-4 h-4 text-[#7C1D3A]" /> Recipients
        </CardTitle>
        <p className="text-[11px] text-slate-500 mt-1 leading-snug">
          <b>To</b> = the person(s) who need to <b>act</b> on this event (owner). Visible in the To header so
          ownership is clear. <b>Cc</b> = people who just need to <b>know</b>. Reply-All threads keep everyone in the
          loop. WhatsApp only fires to the <b>To</b> set (with a fallback to Cc if no To recipient has a phone).
        </p>
      </CardHeader>
      <CardContent className="pt-0 grid grid-cols-1 md:grid-cols-2 gap-6">
        <RecipientsBucket
          label="To — action owners"
          bucketKey="to"
          bucket={toBucket}
          setRcp={setRcp}
          detail={detail}
          users={users}
          count={toCount}
          accent="rose"
        />
        <RecipientsBucket
          label="Cc — informational"
          bucketKey="cc"
          bucket={ccBucket}
          setRcp={setRcp}
          detail={detail}
          users={users}
          count={ccCount}
          accent="slate"
        />
      </CardContent>
    </Card>
  );
}


function RecipientsBucket({ label, bucketKey, bucket, setRcp, detail, users, count, accent }) {
  const [emailInput, setEmailInput] = useState("");
  const [userSearch, setUserSearch] = useState("");

  const filteredUsers = useMemo(() => {
    const s = userSearch.trim().toLowerCase();
    if (!s) return users.slice(0, 20);
    return users.filter(
      (u) => (u.email || "").toLowerCase().includes(s) || (u.name || "").toLowerCase().includes(s)
    ).slice(0, 20);
  }, [users, userSearch]);

  const roles = bucket.roles || [];
  const user_ids = bucket.user_ids || [];
  const fixed_emails = bucket.fixed_emails || [];
  const dynamic_tokens = bucket.dynamic_tokens || [];

  const toggleRole = (r) => {
    const has = roles.includes(r);
    setRcp(bucketKey, "roles", has ? roles.filter((x) => x !== r) : [...roles, r]);
  };
  const toggleUser = (id) => {
    const has = user_ids.includes(id);
    setRcp(bucketKey, "user_ids", has ? user_ids.filter((x) => x !== id) : [...user_ids, id]);
  };
  const addEmail = () => {
    const v = emailInput.trim();
    if (!v || !v.includes("@") || fixed_emails.includes(v)) return;
    setRcp(bucketKey, "fixed_emails", [...fixed_emails, v]);
    setEmailInput("");
  };
  const removeEmail = (e) => setRcp(bucketKey, "fixed_emails", fixed_emails.filter((x) => x !== e));
  const toggleDyn = (t) => {
    const has = dynamic_tokens.includes(t);
    setRcp(bucketKey, "dynamic_tokens", has ? dynamic_tokens.filter((x) => x !== t) : [...dynamic_tokens, t]);
  };

  const selectedUsers = users.filter((u) => user_ids.includes(u.id));

  const activeClass = accent === "rose"
    ? "bg-[#7C1D3A] text-white border-[#7C1D3A]"
    : "bg-slate-700 text-white border-slate-700";
  const badgeClass = accent === "rose"
    ? "bg-[#7C1D3A]/10 text-[#7C1D3A]"
    : "bg-slate-200 text-slate-700";

  return (
    <div className={`rounded-lg border ${accent === "rose" ? "border-[#7C1D3A]/20 bg-rose-50/20" : "border-slate-200 bg-slate-50/50"} p-4`} data-testid={`bucket-${bucketKey}`}>
      <div className="flex items-center justify-between mb-3">
        <div className={`text-xs font-heading font-semibold ${accent === "rose" ? "text-[#7C1D3A]" : "text-slate-700"}`}>
          {label}
        </div>
        <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${badgeClass} font-mono`} data-testid={`bucket-${bucketKey}-count`}>
          {count} configured
        </span>
      </div>

      {/* Roles */}
      <div className="mb-4">
        <Label className="text-[11px] font-semibold text-slate-700 mb-1.5 block">By role</Label>
        <div className="flex flex-wrap gap-1">
          {ROLES.map((r) => {
            const on = roles.includes(r.key);
            return (
              <button
                key={r.key}
                type="button"
                onClick={() => toggleRole(r.key)}
                data-testid={`tpl-${bucketKey}-role-${r.key}`}
                className={`px-2 py-0.5 rounded-full text-[11px] border transition ${on ? activeClass : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}
              >
                {r.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Dynamic tokens */}
      {detail.dynamic_recipient_tokens?.length > 0 && (
        <div className="mb-4">
          <Label className="text-[11px] font-semibold text-slate-700 mb-1.5 block flex items-center gap-1">
            <Zap className="w-3 h-3 text-amber-500" /> Dynamic
          </Label>
          <div className="flex flex-wrap gap-1">
            {detail.dynamic_recipient_tokens.map((t) => {
              const on = dynamic_tokens.includes(t.key);
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => toggleDyn(t.key)}
                  title={t.description}
                  data-testid={`tpl-${bucketKey}-dyn-${t.key}`}
                  className={`px-2 py-0.5 rounded-full text-[11px] border transition font-mono ${on ? "bg-amber-500 text-white border-amber-500" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}
                >
                  {t.key}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Specific users */}
      <div className="mb-4">
        <Label className="text-[11px] font-semibold text-slate-700 mb-1.5 block">Specific users</Label>
        {selectedUsers.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-1.5">
            {selectedUsers.map((u) => (
              <span key={u.id} className={`inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded-full ${badgeClass}`}>
                {u.name || u.email}
                <button onClick={() => toggleUser(u.id)} className="hover:text-rose-600">×</button>
              </span>
            ))}
          </div>
        )}
        <Input
          value={userSearch}
          onChange={(e) => setUserSearch(e.target.value)}
          placeholder="Search users..."
          className="text-xs h-8"
          data-testid={`tpl-${bucketKey}-user-search`}
        />
        {userSearch && filteredUsers.length > 0 && (
          <div className="mt-1 max-h-32 overflow-y-auto border border-slate-200 rounded-md bg-white">
            {filteredUsers.map((u) => (
              <button
                key={u.id}
                type="button"
                onClick={() => toggleUser(u.id)}
                className="w-full text-left px-2 py-1 text-[11px] hover:bg-slate-50 flex items-center justify-between"
              >
                <span><b>{u.name || u.email}</b> · <span className="text-slate-500">{u.email}</span></span>
                {user_ids.includes(u.id) && <CheckCircle2 className="w-3 h-3 text-emerald-600" />}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Fixed emails */}
      <div>
        <Label className="text-[11px] font-semibold text-slate-700 mb-1.5 block">Fixed emails</Label>
        {fixed_emails.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-1.5">
            {fixed_emails.map((e) => (
              <span key={e} className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-700">
                {e}
                <button onClick={() => removeEmail(e)} className="hover:text-rose-600">×</button>
              </span>
            ))}
          </div>
        )}
        <div className="flex gap-1.5">
          <Input
            type="email"
            value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addEmail(); } }}
            placeholder="external@partner.in"
            className="text-xs h-8"
            data-testid={`tpl-${bucketKey}-fixed-email-input`}
          />
          <Button variant="outline" size="sm" onClick={addEmail} className="h-8 text-xs px-2" data-testid={`tpl-${bucketKey}-fixed-email-add`}>Add</Button>
        </div>
      </div>
    </div>
  );
}



// ---------------------------------------------------------------------------
// WhatsApp status pill (top-right of the page)
// ---------------------------------------------------------------------------
function WhatsAppStatusBadge({ status, onRefresh }) {
  if (!status) {
    return (
      <div className="text-xs text-slate-400 flex items-center gap-1.5">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Checking WhatsApp…
      </div>
    );
  }
  const configured = !!status.configured;
  return (
    <div
      className={`flex items-center gap-2 px-3 py-2 rounded-md border text-xs ${
        configured ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"
      }`}
      data-testid="wa-status-badge"
    >
      <MessageCircle className={`w-4 h-4 ${configured ? "text-emerald-600" : "text-amber-600"}`} />
      <div className="leading-tight">
        <div className={`font-semibold ${configured ? "text-emerald-900" : "text-amber-900"}`}>
          {configured ? "Automate.chats connected" : "WhatsApp not configured"}
        </div>
        <div className={`text-[10px] ${configured ? "text-emerald-700" : "text-amber-700"}`}>
          {configured
            ? `Key ${status.key_masked} · CC ${status.default_country_code}`
            : "Set AUTOMATECHATS_API_KEY in backend .env to enable WhatsApp"}
        </div>
      </div>
      <button
        type="button"
        onClick={onRefresh}
        className="ml-2 p-1 rounded hover:bg-white/60 transition"
        title="Refresh"
        data-testid="wa-status-refresh"
      >
        <RefreshCw className="w-3 h-3 text-slate-500" />
      </button>
    </div>
  );
}


// ---------------------------------------------------------------------------
// WhatsApp per-trigger panel (main WA UI)
// ---------------------------------------------------------------------------
function WhatsAppPanel({ detail, setWa, saveParent, dirty, waConfigured }) {
  const wa = detail.whatsapp || { enabled: false, template_id: "", variable_map: "" };
  const [tokenSamples, setTokenSamples] = useState([]);
  const [recentSends, setRecentSends] = useState([]);
  const [loadingRecent, setLoadingRecent] = useState(false);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testPhone, setTestPhone] = useState("");
  const [testVars, setTestVars] = useState("");
  const [testSending, setTestSending] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [templateVars, setTemplateVars] = useState(null); // Automate.chats fetch result
  const [fetchingVars, setFetchingVars] = useState(false);
  const [mapError, setMapError] = useState("");

  // Load token samples + recent sends whenever we switch triggers
  useEffect(() => {
    setRecentSends([]);
    setTokenSamples([]);
    setTemplateVars(null);
    setTestResult(null);
    (async () => {
      try {
        const r = await api.get(`/admin/whatsapp/token-samples`, {
          params: { trigger_key: detail.trigger_key },
        });
        setTokenSamples(r.data.tokens || []);
      } catch { /* ignore */ }
      loadRecent();
    })();
    // eslint-disable-next-line
  }, [detail.trigger_key]);

  const loadRecent = async () => {
    setLoadingRecent(true);
    try {
      const r = await api.get(`/admin/whatsapp/recent-sends`, {
        params: { trigger_key: detail.trigger_key, limit: 5 },
      });
      setRecentSends(r.data || []);
    } catch { /* ignore */ } finally { setLoadingRecent(false); }
  };

  // Live JSON validation of the variable_map textarea
  useEffect(() => {
    const raw = (wa.variable_map || "").trim();
    if (!raw) { setMapError(""); return; }
    try { JSON.parse(raw); setMapError(""); }
    catch (e) { setMapError(e.message || "Invalid JSON"); }
  }, [wa.variable_map]);

  const fetchAcVars = async () => {
    const tid = (wa.template_id || "").trim();
    if (!tid) { toast.error("Enter a template ID first"); return; }
    setFetchingVars(true);
    try {
      const r = await api.get(`/admin/whatsapp/template-variables`, {
        params: { template_id: tid },
      });
      setTemplateVars(r.data);
      if (r.data?.ok === false) toast.error("Automate.chats lookup failed");
      else toast.success("Fetched template metadata");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fetch failed");
    } finally { setFetchingVars(false); }
  };

  const openTestDialog = () => {
    setTestPhone("");
    // Prefill with a sample variable_map so the user just tweaks & sends.
    const rawMap = (wa.variable_map || "").trim();
    if (rawMap) {
      setTestVars(rawMap);
    } else if (tokenSamples.length > 0) {
      const asObj = {};
      tokenSamples.slice(0, 8).forEach((t) => { asObj[t.token] = t.sample; });
      setTestVars(JSON.stringify(asObj, null, 2));
    } else {
      setTestVars("");
    }
    setTestResult(null);
    setTestDialogOpen(true);
  };

  const sendTest = async () => {
    const tid = (wa.template_id || "").trim();
    if (!tid) { toast.error("Save a template ID first"); return; }
    if (!testPhone.trim()) { toast.error("Enter a phone number"); return; }
    let variable_map = null;
    const raw = testVars.trim();
    if (raw) {
      try { variable_map = JSON.parse(raw); }
      catch (e) { toast.error("Test vars must be valid JSON"); return; }
    }
    setTestSending(true); setTestResult(null);
    try {
      const r = await api.post(`/admin/whatsapp/test`, {
        template_id: tid,
        phone: testPhone.trim(),
        variable_map,
        trigger_key: detail.trigger_key,
      });
      setTestResult(r.data);
      if (r.data?.ok) toast.success("WhatsApp test dispatched");
      else toast.error("WhatsApp send failed — see details");
      loadRecent();
    } catch (e) {
      setTestResult({ ok: false, message: e.response?.data?.detail || e.message });
    } finally {
      setTestSending(false);
    }
  };

  // Inline "quick" test — uses the saved variable_map without any overrides.
  // Doesn't open the dialog; just fires and shows the result inline.
  const sendInlineTest = async () => {
    const tid = (wa.template_id || "").trim();
    if (!tid) { toast.error("Save a template ID first"); return; }
    if (!testPhone.trim()) { toast.error("Enter a phone number"); return; }
    let variable_map = null;
    const raw = (wa.variable_map || "").trim();
    if (raw) {
      try { variable_map = JSON.parse(raw); }
      catch { /* saved map is invalid JSON — send without a map, server will fall back */ }
    }
    setTestSending(true); setTestResult(null);
    try {
      const r = await api.post(`/admin/whatsapp/test`, {
        template_id: tid,
        phone: testPhone.trim(),
        variable_map,
        trigger_key: detail.trigger_key,
      });
      setTestResult(r.data);
      if (r.data?.ok) toast.success("WhatsApp test dispatched");
      else toast.error("WhatsApp send failed — see details");
      loadRecent();
    } catch (e) {
      setTestResult({ ok: false, message: e.response?.data?.detail || e.message });
    } finally {
      setTestSending(false);
    }
  };

  const insertTokenIntoMap = (token) => {
    const placeholder = `{{${token}}}`;
    const raw = (wa.variable_map || "").trim();
    // If empty, seed with a JSON object having this key.
    if (!raw) {
      setWa("variable_map", JSON.stringify({ [token]: placeholder }, null, 2));
      return;
    }
    // If the current map is valid JSON, mutate it structurally so we don't break
    // parsing. Bare arrays get the placeholder appended; objects get the key set.
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        parsed.push(placeholder);
        setWa("variable_map", JSON.stringify(parsed, null, 2));
      } else if (parsed && typeof parsed === "object") {
        parsed[token] = placeholder;
        setWa("variable_map", JSON.stringify(parsed, null, 2));
      } else {
        // Fallback for JSON primitives — just replace with an object.
        setWa("variable_map", JSON.stringify({ [token]: placeholder }, null, 2));
      }
    } catch {
      // Current textarea contents aren't valid JSON — leave the raw text but
      // append the placeholder text so the admin can slot it in themselves.
      setWa("variable_map", (wa.variable_map || "") + `\n"${token}": "${placeholder}"`);
    }
  };

  const acVarList = Array.isArray(templateVars?.response?.data?.variables)
    ? templateVars.response.data.variables
    : Array.isArray(templateVars?.response?.variables)
      ? templateVars.response.variables
      : [];

  return (
    <Card data-testid="wa-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-sm font-heading flex items-center gap-2">
            <MessageCircle className="w-4 h-4 text-emerald-600" /> WhatsApp (Automate.chats)
            {wa.enabled ? (
              <Badge variant="outline" className="border-emerald-300 text-emerald-700 bg-emerald-50 text-[10px]">
                Enabled
              </Badge>
            ) : (
              <Badge variant="outline" className="border-slate-300 text-slate-600 bg-slate-50 text-[10px]">
                Disabled
              </Badge>
            )}
          </CardTitle>
          <div className="flex items-center gap-2">
            <Switch
              checked={!!wa.enabled}
              onCheckedChange={(v) => setWa("enabled", v)}
              data-testid="wa-enabled-switch"
            />
            <span className="text-xs font-medium text-slate-600">
              {wa.enabled ? "Fan-out to WhatsApp on this trigger" : "Off"}
            </span>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0 space-y-5">
        {!waConfigured && (
          <Alert className="border-amber-200 bg-amber-50">
            <Info className="w-4 h-4 !text-amber-600" />
            <AlertDescription className="text-amber-900 text-xs">
              Server-side WhatsApp key is missing. Ask an admin to set
              <code className="font-mono mx-1 bg-white/70 px-1 rounded">AUTOMATECHATS_API_KEY</code>
              in the backend <code className="font-mono">.env</code> and restart. You can still configure the template below.
            </AlertDescription>
          </Alert>
        )}

        {/* Template ID + fetch button */}
        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-end">
          <div>
            <Label className="text-xs font-semibold text-slate-700 mb-1.5 block flex items-center gap-1.5">
              <PlugZap className="w-3.5 h-3.5 text-emerald-600" /> Automate.chats template ID
            </Label>
            <Input
              value={wa.template_id || ""}
              onChange={(e) => setWa("template_id", e.target.value)}
              placeholder="e.g. 68b1f9c6c8a3e2001d3f4a12"
              className="font-mono text-xs"
              data-testid="wa-template-id"
            />
            <p className="text-[10px] text-slate-500 mt-1">
              Grab the ID from your Automate.chats dashboard → Templates → Details → ID.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchAcVars}
            disabled={!wa.template_id || fetchingVars}
            data-testid="wa-fetch-vars-btn"
          >
            {fetchingVars ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Eye className="w-3.5 h-3.5 mr-1.5" />}
            Inspect template
          </Button>
        </div>

        {/* Template variables preview (from Automate.chats) */}
        {templateVars && (
          <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
            <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-1.5 flex items-center gap-1">
              <FileJson className="w-3 h-3" /> Automate.chats template metadata
            </div>
            {acVarList.length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {acVarList.map((v, i) => (
                  <Badge key={i} variant="outline" className="text-[10px] font-mono border-slate-300 bg-white">
                    {typeof v === "string" ? v : (v?.name || v?.key || JSON.stringify(v))}
                  </Badge>
                ))}
              </div>
            ) : (
              <pre className="text-[10px] font-mono text-slate-700 whitespace-pre-wrap max-h-32 overflow-y-auto">
                {JSON.stringify(templateVars, null, 2)}
              </pre>
            )}
          </div>
        )}

        {/* Variable mapping */}
        <div>
          <Label className="text-xs font-semibold text-slate-700 mb-1.5 block flex items-center gap-1.5">
            <FileJson className="w-3.5 h-3.5 text-emerald-600" /> Variable mapping (JSON)
          </Label>
          <p className="text-[11px] text-slate-500 mb-2">
            Use <code className="font-mono bg-slate-100 px-1 rounded">{"{{token}}"}</code> placeholders — the same ones from your email tokens.
            Send a bare array like <code className="font-mono bg-slate-100 px-1 rounded">["Ravi","1250"]</code> for
            positional templates, or a rich object for header / body / button substitution.
          </p>
          <Textarea
            value={wa.variable_map || ""}
            onChange={(e) => setWa("variable_map", e.target.value)}
            placeholder={`Example:\n{\n  "recipient_name": "{{recipient_name}}",\n  "asset_name": "{{asset_name}}",\n  "urgency": "{{urgency}}"\n}`}
            className="font-mono text-xs h-40 resize-none"
            spellCheck={false}
            data-testid="wa-variable-map"
          />
          {mapError && (
            <div className="mt-1 text-[11px] text-rose-600 font-mono flex items-center gap-1">
              <XCircle className="w-3 h-3" /> {mapError}
            </div>
          )}
          {tokenSamples.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              <span className="text-[10px] text-slate-400 self-center mr-1">Insert token:</span>
              {tokenSamples.map((t) => (
                <button
                  key={t.token}
                  type="button"
                  onClick={() => insertTokenIntoMap(t.token)}
                  title={`Sample: ${t.sample}`}
                  className="text-[10px] font-mono px-1.5 py-0.5 rounded border border-slate-200 bg-slate-50 hover:bg-emerald-50 hover:border-emerald-300 hover:text-emerald-700 transition"
                  data-testid={`wa-token-${t.token}`}
                >
                  {"{{"}
                  {t.token}
                  {"}}"}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Inline WhatsApp test-send — mirrors the email test-send UX */}
        <div className="pt-3 border-t border-slate-100">
          <div className="flex items-center justify-between mb-1.5">
            <Label className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
              <Send className="w-3.5 h-3.5 text-emerald-600" /> Send test WhatsApp
            </Label>
            <button
              type="button"
              onClick={openTestDialog}
              disabled={!wa.template_id || !waConfigured}
              className="text-[10px] text-slate-500 hover:text-emerald-700 disabled:opacity-40 disabled:hover:text-slate-500"
              title="Advanced — override variables JSON and inspect the raw response"
              data-testid="wa-test-open-btn"
            >
              Advanced test →
            </button>
          </div>
          <p className="text-[11px] text-slate-500 mb-2">
            Enter <b>any number</b> for a dry-run — your own, a colleague&apos;s, a shared ops handset. Nothing is pulled from your profile.
            Uses the template ID + variable mapping saved above. Save any pending edits first.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <Input
              value={testPhone}
              onChange={(e) => setTestPhone(e.target.value)}
              placeholder="Enter any number to test — e.g. +919876543210"
              className="max-w-md"
              data-testid="wa-test-phone-inline"
            />
            <Button
              onClick={sendInlineTest}
              disabled={!wa.template_id || !waConfigured || !testPhone.trim() || testSending}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="wa-test-send-inline-btn"
            >
              {testSending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Send test
            </Button>
          </div>
          {!waConfigured && (
            <p className="text-[10px] text-amber-700 mt-1.5">
              <Info className="w-3 h-3 inline mr-1" /> Server-side WhatsApp key is missing — the button unlocks once <code className="font-mono">AUTOMATECHATS_API_KEY</code> is set.
            </p>
          )}
          {!wa.template_id && waConfigured && (
            <p className="text-[10px] text-slate-500 mt-1.5">
              <Info className="w-3 h-3 inline mr-1" /> Paste a Template ID above and click Save first to unlock testing.
            </p>
          )}
          {testResult && (
            <Alert className={`mt-3 ${testResult.ok ? "border-emerald-200 bg-emerald-50" : "border-rose-200 bg-rose-50"}`}>
              {testResult.ok
                ? <CheckCircle2 className="w-4 h-4 !text-emerald-600" />
                : <XCircle className="w-4 h-4 !text-rose-600" />}
              <AlertDescription className={`${testResult.ok ? "text-emerald-900" : "text-rose-900"} text-xs`}>
                <div className="font-semibold mb-1">
                  {testResult.ok ? "Dispatched to Automate.chats" : "Delivery failed"} · status {testResult.status_code || "-"}
                </div>
                <pre className="text-[10px] font-mono bg-white/70 border rounded p-2 whitespace-pre-wrap break-words max-h-40 overflow-y-auto">
                  {JSON.stringify(testResult.response || testResult.message || testResult, null, 2)}
                </pre>
              </AlertDescription>
            </Alert>
          )}
        </div>

        <p className="text-[11px] text-slate-500 pt-2 border-t border-slate-100">
          <Info className="w-3 h-3 inline mr-1 text-slate-400" />
          When enabled, WhatsApp fires to the same recipients as the email above — as long as their user profile has a phone number.
        </p>

        {/* Recent WhatsApp sends */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <Label className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5 text-emerald-600" /> Recent WhatsApp sends
            </Label>
            <button
              type="button"
              onClick={loadRecent}
              className="text-[10px] text-slate-500 hover:text-slate-800 flex items-center gap-1"
              data-testid="wa-recent-refresh"
            >
              <RefreshCw className={`w-3 h-3 ${loadingRecent ? "animate-spin" : ""}`} /> Refresh
            </button>
          </div>
          {recentSends.length === 0 ? (
            <div className="text-[11px] text-slate-400 border border-dashed border-slate-200 rounded-md p-3 text-center">
              No WhatsApp sends recorded yet for this trigger.
            </div>
          ) : (
            <div className="space-y-1.5 max-h-52 overflow-y-auto">
              {recentSends.map((r, i) => (
                <WhatsAppLogRow key={r.id || i} row={r} />
              ))}
            </div>
          )}
        </div>
      </CardContent>

      {/* Test dialog */}
      <Dialog open={testDialogOpen} onOpenChange={setTestDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="wa-test-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageCircle className="w-4 h-4 text-emerald-600" /> Send test WhatsApp
            </DialogTitle>
            <DialogDescription className="text-xs">
              Sends this template through Automate.chats to a phone number you choose.
              Unsaved template ID / mapping changes are <b>not</b> used — save first if you tweaked them.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {dirty && (
              <Alert className="border-amber-200 bg-amber-50 py-2">
                <Info className="w-3.5 h-3.5 !text-amber-600" />
                <AlertDescription className="text-amber-900 text-[11px]">
                  You have unsaved changes. Save the template before testing to make sure the mapping matches.
                </AlertDescription>
              </Alert>
            )}
            <div>
              <Label className="text-xs font-semibold text-slate-700 mb-1 block flex items-center gap-1.5">
                <Phone className="w-3.5 h-3.5" /> Test phone number
              </Label>
              <Input
                value={testPhone}
                onChange={(e) => setTestPhone(e.target.value)}
                placeholder="Enter any number to test — e.g. +919876543210"
                data-testid="wa-test-phone"
                autoFocus
              />
              <p className="text-[10px] text-slate-500 mt-1">
                Dry-run to <b>any</b> phone — your own, a colleague&apos;s, a shared ops handset. Nothing is pulled from your profile.
                Include country code with a &quot;+&quot; prefix; without it, the server default (
                <code className="font-mono">+91</code>) is used.
              </p>
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-700 mb-1 block">Variables (JSON, optional)</Label>
              <Textarea
                value={testVars}
                onChange={(e) => setTestVars(e.target.value)}
                className="font-mono text-xs h-40 resize-none"
                spellCheck={false}
                data-testid="wa-test-vars"
              />
            </div>
            {testResult && (
              <Alert className={testResult.ok ? "border-emerald-200 bg-emerald-50" : "border-rose-200 bg-rose-50"}>
                {testResult.ok
                  ? <CheckCircle2 className="w-4 h-4 !text-emerald-600" />
                  : <XCircle className="w-4 h-4 !text-rose-600" />}
                <AlertDescription className={`${testResult.ok ? "text-emerald-900" : "text-rose-900"} text-xs`}>
                  <div className="font-semibold mb-1">
                    {testResult.ok ? "Delivered" : "Delivery failed"} · status {testResult.status_code || "-"}
                  </div>
                  <pre className="text-[10px] font-mono bg-white/70 border rounded p-2 whitespace-pre-wrap break-words max-h-40 overflow-y-auto">
                    {JSON.stringify(testResult.response || testResult.message || testResult, null, 2)}
                  </pre>
                </AlertDescription>
              </Alert>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTestDialogOpen(false)} data-testid="wa-test-close-btn">Close</Button>
            <Button
              onClick={sendTest}
              disabled={testSending || !testPhone.trim()}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="wa-test-send-btn"
            >
              {testSending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Send test
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}


function WhatsAppLogRow({ row }) {
  const status = row.status || "-";
  const attempts = row.attempts || [];
  const first = attempts[0] || {};
  const err = first.err || row.reason || "";
  const okCount = (row.delivered || []).length;
  const failCount = (row.failed || []).length;
  const noPhone = (row.no_phone || []).length;
  const chip = ({
    sent: "border-emerald-300 text-emerald-700 bg-emerald-50",
    partial: "border-amber-300 text-amber-700 bg-amber-50",
    failed: "border-rose-300 text-rose-700 bg-rose-50",
    skipped: "border-slate-300 text-slate-600 bg-slate-50",
  }[status] || "border-slate-300 text-slate-600 bg-slate-50");

  return (
    <div className="border border-slate-200 rounded-md p-2 bg-white text-[11px]" data-testid="wa-log-row">
      <div className="flex items-center gap-2 flex-wrap">
        <Badge variant="outline" className={`text-[10px] ${chip}`}>{status}</Badge>
        <span className="text-slate-500">{row.at ? new Date(row.at).toLocaleString() : ""}</span>
        {row.template_id && (
          <span className="font-mono text-[10px] text-slate-500 truncate max-w-[180px]">
            tpl:{row.template_id.slice(0, 10)}…
          </span>
        )}
        <span className="ml-auto text-slate-500">
          ok {okCount} · fail {failCount}{noPhone ? ` · no-phone ${noPhone}` : ""}
        </span>
      </div>
      {err && (
        <div className="mt-1 text-rose-700 font-mono text-[10px] truncate" title={typeof err === "string" ? err : JSON.stringify(err)}>
          {typeof err === "string" ? err : JSON.stringify(err)}
        </div>
      )}
    </div>
  );
}


/* ------------------------------------------------------------------ */
/* Feb 2026 — Reminder schedule editor (days-before-due).             */
/* Used by maintenance_due & maintenance_due_digest triggers so admins */
/* can pick which threshold(s) fire the reminder (e.g. 7, 3, 1, 0).    */
/* ------------------------------------------------------------------ */
const SCHEDULE_PRESETS = [0, 1, 2, 3, 5, 7, 10, 14, 21, 30, 45, 60, 90];

function ScheduleDaysBeforeCard({ value, defaults, onChange }) {
  const sorted = [...(value || [])].sort((a, b) => b - a);
  const [customVal, setCustomVal] = useState("");

  const toggle = (n) => {
    const set = new Set(sorted);
    if (set.has(n)) set.delete(n); else set.add(n);
    const next = Array.from(set).filter((x) => Number.isFinite(x)).sort((a, b) => b - a);
    onChange(next.length ? next : [0]);
  };

  const addCustom = () => {
    const n = parseInt(customVal, 10);
    if (Number.isNaN(n) || n < 0 || n > 365) {
      toast.error("Enter a whole number between 0 and 365");
      return;
    }
    if (!sorted.includes(n)) {
      onChange([...sorted, n].sort((a, b) => b - a));
    }
    setCustomVal("");
  };

  const resetDefaults = () => onChange([...(defaults || [0])].sort((a, b) => b - a));

  const label = (n) => n === 0 ? "On due date" : `${n} day${n === 1 ? "" : "s"} before`;

  return (
    <Card data-testid="schedule-days-before-card">
      <CardHeader className="border-b border-slate-100 pb-3">
        <CardTitle className="font-heading text-base flex items-center gap-2">
          <Clock className="w-4 h-4 text-[#7C1D3A]" /> Reminder Schedule
        </CardTitle>
        <p className="text-xs text-slate-500 mt-1">
          Pick when this reminder should fire — before the due date, on the due date, or a mix. Every ticked value fires a separate alert on its day.
        </p>
      </CardHeader>
      <CardContent className="p-5 space-y-4">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Currently firing on</div>
          {sorted.length === 0 ? (
            <div className="text-xs text-slate-500 italic">No reminders configured. The reminder will not fire until you tick at least one option.</div>
          ) : (
            <div className="flex flex-wrap gap-2" data-testid="active-schedule-chips">
              {sorted.map((n) => (
                <span key={n} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#fcf0f3] border border-[#f5c4d3] text-[#7C1D3A] text-xs font-medium">
                  {label(n)}
                  <button
                    type="button"
                    onClick={() => toggle(n)}
                    className="opacity-60 hover:opacity-100 -mr-0.5 text-[10px]"
                    aria-label={`Remove ${label(n)}`}
                    data-testid={`schedule-remove-${n}`}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Quick pick</div>
          <div className="flex flex-wrap gap-1.5">
            {SCHEDULE_PRESETS.map((n) => {
              const on = sorted.includes(n);
              return (
                <button
                  key={n}
                  type="button"
                  onClick={() => toggle(n)}
                  className={`px-2.5 py-1 rounded-md border text-xs font-medium transition ${on ? "bg-[#7C1D3A] text-white border-[#7C1D3A]" : "bg-white text-slate-600 border-slate-200 hover:border-[#7C1D3A]"}`}
                  data-testid={`schedule-preset-${n}`}
                >
                  {label(n)}
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-2 pt-1">
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Custom (days before)</Label>
            <Input
              type="number"
              min="0"
              max="365"
              value={customVal}
              onChange={(e) => setCustomVal(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustom(); } }}
              placeholder="e.g. 14"
              className="w-32 h-9 text-sm"
              data-testid="schedule-custom-input"
            />
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addCustom}
            disabled={!customVal.trim()}
            data-testid="schedule-add-custom-btn"
          >
            Add
          </Button>
          <div className="ml-auto">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={resetDefaults}
              data-testid="schedule-reset-defaults-btn"
            >
              <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Reset to default
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
